import translate from 'core/translate'
import { clientToLogical } from '../elements/coords'
import { hitListItem, hitTableCell } from '../elements/tableListOps'
import { EditorTheme } from '../templateEditor.theme'
import { TemplateElement } from '../templateEditor.types'
import { ContextMenuAction } from './templateEditor__contextMenu'

type ZOrderApi = {
    bringToFront: (ids: string[]) => void
    bringForward: (ids: string[]) => void
    sendBackward: (ids: string[]) => void
    sendToBack: (ids: string[]) => void
}

type StructureApi = {
    insertTableRowAt: (id: string, index: number) => void
    insertTableColAt: (id: string, index: number) => void
    removeTableRowAt: (id: string, index: number) => void
    removeTableColAt: (id: string, index: number) => void
    insertListItemAt: (id: string, index: number) => void
    removeListItemAt: (id: string, index: number) => void
}

type EditApi = {
    duplicateSelection: () => void
    deleteSelection: () => void
    startImageReplaceMode?: (elementId: string) => void
}

type BuildElementContextMenuParams = {
    elementId: string
    element: TemplateElement | undefined
    clientX: number
    clientY: number
    frameRect: DOMRect | null
    displayScale: number
    zoom: number
    theme: EditorTheme
    zOrder: ZOrderApi
    structure: StructureApi
    edit: EditApi
}

const action = (
    id: string,
    labelKey: string,
    onClick: () => void,
    extras?: Partial<Pick<ContextMenuAction, 'disabled' | 'danger'>>
): ContextMenuAction => ({
    id,
    label: translate(labelKey),
    onClick,
    ...extras,
})

const zOrderGroup = (elementId: string, api: ZOrderApi): ContextMenuAction[] => [
    action('front', 'templateEditorBringFront', () => api.bringToFront([elementId])),
    action('forward', 'templateEditorBringForward', () => api.bringForward([elementId])),
    action('backward', 'templateEditorSendBackward', () => api.sendBackward([elementId])),
    action('back', 'templateEditorSendBack', () => api.sendToBack([elementId])),
]

const editGroup = (elementId: string, element: TemplateElement | undefined, api: EditApi): ContextMenuAction[] => {
    const actions: ContextMenuAction[] = [
        action('duplicate', 'templateEditorDuplicate', () => api.duplicateSelection()),
    ]

    if (element?.type === 'image' && api.startImageReplaceMode) {
        actions.push(action('replace', 'templateEditorReplaceImage', () => api.startImageReplaceMode?.(elementId)))
    }

    actions.push(action('delete', 'delete', () => api.deleteSelection(), { danger: true }))
    return actions
}

/** Пункты table/list: add в конец, remove по ячейке/пункту под курсором. */
const structureGroup = (
    element: TemplateElement,
    localX: number,
    localY: number,
    theme: EditorTheme,
    api: StructureApi
): ContextMenuAction[] => {
    if (element.type === 'table') {
        const { row, col } = hitTableCell(element, localX, localY)
        return [
            action('add-row', 'templateEditorAddRow', () => api.insertTableRowAt(element.id, element.rows)),
            action('add-col', 'templateEditorAddCol', () => api.insertTableColAt(element.id, element.cols)),
            action('remove-row', 'templateEditorRemoveRow', () => api.removeTableRowAt(element.id, row), {
                disabled: element.rows <= 1,
            }),
            action('remove-col', 'templateEditorRemoveCol', () => api.removeTableColAt(element.id, col), {
                disabled: element.cols <= 1,
            }),
        ]
    }

    if (element.type === 'list') {
        const index = hitListItem(element, localX, localY, theme)
        return [
            action('add-item', 'templateEditorAddListItem', () =>
                api.insertListItemAt(element.id, element.items.length)
            ),
            action('remove-item', 'templateEditorRemoveListItem', () => api.removeListItemAt(element.id, index), {
                disabled: element.items.length <= 1,
            }),
        ]
    }

    return []
}

const localPointInElement = (
    element: TemplateElement,
    clientX: number,
    clientY: number,
    frameRect: DOMRect,
    displayScale: number,
    zoom: number
) => {
    const logical = clientToLogical(clientX, clientY, frameRect, displayScale, zoom)
    return {
        x: logical.x - element.x,
        y: logical.y - element.y,
    }
}

/** Группы ПКМ: z-order → duplicate/delete → table/list ops. */
export const buildElementContextMenuGroups = ({
    elementId,
    element,
    clientX,
    clientY,
    frameRect,
    displayScale,
    zoom,
    theme,
    zOrder,
    structure,
    edit,
}: BuildElementContextMenuParams): ContextMenuAction[][] => {
    const groups: ContextMenuAction[][] = [zOrderGroup(elementId, zOrder), editGroup(elementId, element, edit)]

    const canEditStructure = element && (element.type === 'table' || element.type === 'list') && frameRect !== null

    if (canEditStructure && element && frameRect) {
        const local = localPointInElement(element, clientX, clientY, frameRect, displayScale, zoom)
        groups.push(structureGroup(element, local.x, local.y, theme, structure))
    }

    return groups
}
